// EmpreendimentoInformacoesEspecificasForm.tsx

import React, { useMemo, useCallback } from 'react';
import Select, { OnChangeValue, GroupBase } from 'react-select';

// Nota: Assumindo que SelectOption e os tipos GroupBase do react-select estão disponíveis.
interface SelectOption {
    id: number | string;
    nome: string;
}

// Tipo de opção compatível com o react-select
interface CustomSelectOption {
    value: string | number;
    label: string;
}

// Tipo de grupo para o react-select
interface ItemOptionGroup extends GroupBase<CustomSelectOption> {
    label: string;
    options: CustomSelectOption[];
}

interface Props {
    formData: any;
    handleInputChange: (field: string, value: any) => void;
    errors: Record<string, string>;
    fasesOptions: SelectOption[];
    tiposOptions: SelectOption[];
    itensInfraestrutura: SelectOption[];
    itensSeguranca: SelectOption[];
    itensLazer: SelectOption[];
}

/**
 * Mapeia o array SelectOption customizado para o formato de opção do react-select.
 */
const mapOptionsToSelect = (options: SelectOption[]): CustomSelectOption[] =>
    options.map(opt => ({ value: opt.id, label: opt.nome }));

/**
 * Mapeia IDs selecionados de volta para o formato de opção do react-select,
 * usando todas as opções disponíveis para encontrar o label.
 */
const mapIdsToOptions = (ids: (string | number)[], allOptions: SelectOption[]): CustomSelectOption[] => {
    const optionMap = new Map(allOptions.map(opt => [opt.id, opt.nome]));
    
    return ids
        // Garante que apenas IDs presentes nas opções sejam mapeados, ou um placeholder se necessário
        .map(id => ({
            value: id,
            label: optionMap.get(id) ?? `[Desconhecido - ID ${id}]`,
        }))
        // Filtra IDs desconhecidos que não deveriam estar no select (opcional)
        .filter(opt => opt.label !== `[Desconhecido - ID ${opt.value}]` || optionMap.has(opt.value));
};

// --- COMPONENTE PRINCIPAL ---

const EmpreendimentoInformacoesEspecificasForm: React.FC<Props> = ({
    formData,
    handleInputChange,
    errors,
    fasesOptions,
    tiposOptions,
    itensInfraestrutura,
    itensSeguranca,
    itensLazer,
}) => {

    // Consolida todas as opções de itens para mapeamento
    const allItemOptions: SelectOption[] = useMemo(() => [
        ...itensInfraestrutura,
        ...itensSeguranca,
        ...itensLazer,
    ], [itensInfraestrutura, itensSeguranca, itensLazer]);

    // Cria as opções agrupadas para o único Select de Itens
    const itemOptionGroups: ItemOptionGroup[] = useMemo(() => [
        { label: 'Infraestrutura', options: mapOptionsToSelect(itensInfraestrutura) },
        { label: 'Segurança', options: mapOptionsToSelect(itensSeguranca) },
        { label: 'Lazer', options: mapOptionsToSelect(itensLazer) },
    ], [itensInfraestrutura, itensSeguranca, itensLazer]);

    // Manipulador unificado para todos os itens
    const handleItemChange = useCallback((selected: OnChangeValue<CustomSelectOption, true>) => {
        const selectedIds = selected.map(opt => opt.value);
        // Atualiza o campo único `item_empreendimento` com todos os IDs
        handleInputChange('item_empreendimento', selectedIds);
    }, [handleInputChange]);

    return (
        <div className="space-y-6 bg-white p-6 shadow-md rounded-lg">
            <h3 className="text-xl font-semibold text-gray-800 border-b pb-2">Informações Específicas</h3>

            {/* Fase do empreendimento (MANTIDO como <select> padrão) */}
            <div>
                <label htmlFor="fase_empreendimento" className="block text-sm font-medium text-gray-700">Fase</label>
                <select
                    id="fase_empreendimento"
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                    value={formData.fase_empreendimento || ''}
                    onChange={e => handleInputChange('fase_empreendimento', Number(e.target.value) || undefined)}
                >
                    <option value="">Selecione uma fase</option>
                    {fasesOptions.map(option => (
                        <option key={option.id} value={option.id}>
                            {option.nome}
                        </option>
                    ))}
                </select>
            </div>
            
            {/* Tipos de empreendimento (REACT-SELECT MULTI-VALOR) */}
            <div>
                <label htmlFor="tipo_empreendimento" className="block text-sm font-medium text-gray-700">Tipos de Empreendimento</label>
                <Select
                    id="tipo_empreendimento"
                    isMulti
                    options={mapOptionsToSelect(tiposOptions)}
                    value={mapIdsToOptions(formData.tipo_empreendimento || [], tiposOptions)}
                    onChange={selected =>
                        handleInputChange('tipo_empreendimento', selected.map(opt => opt.value))
                    }
                    placeholder="Selecione um ou mais tipos..."
                    className="mt-1"
                />
            </div>

            {/* ITENS: INFRAESTRUTURA, SEGURANÇA E LAZER (REACT-SELECT AGRUPADO) */}
            <div className='pt-2 border-t border-gray-200'>
                <label htmlFor="item_empreendimento" className="block text-sm font-medium text-gray-700 mb-1">
                    Itens de Infraestrutura, Segurança e Lazer
                </label>
                <Select<CustomSelectOption, true, ItemOptionGroup>
                    id="item_empreendimento"
                    isMulti
                    options={itemOptionGroups}
                    // O valor é mapeado a partir do array consolidado, usando todas as opções
                    value={mapIdsToOptions(formData.item_empreendimento || [], allItemOptions)}
                    onChange={handleItemChange}
                    placeholder="Selecione os itens disponíveis..."
                    className="mt-1"
                    // Estilização do grupo para melhor visualização (opcional)
                    formatGroupLabel={data => (
                        <div className="font-bold text-sm text-blue-600">{data.label}</div>
                    )}
                />
                {errors.item_empreendimento && (
                    <p className="mt-1 text-sm text-red-600">{errors.item_empreendimento}</p>
                )}
            </div>

            {/* Você pode adicionar aqui campos para `elevadores`, `heading`, `pitch`, etc. */}

        </div>
    );
};

export default EmpreendimentoInformacoesEspecificasForm;